/**
 * Smarter pasting:
 *  - HTML on the clipboard converts to markdown (turndown, same engine as
 *    the Evernote importer) so copying from the web keeps its structure
 *  - pasting a URL over selected text turns the selection into a link
 * Code blocks are exempt: pastes there stay literal.
 */
import { EditorView } from "@codemirror/view";
import type { Extension } from "@codemirror/state";
import { syntaxTree } from "@codemirror/language";
import { EditorSelection } from "@codemirror/state";
import TurndownService from "turndown";
import { stripMarkdownEscapes } from "@hydra-notes/shared";

const URL_RE = /^https?:\/\/\S+$/;

let turndown: TurndownService | null = null;
function getTurndown(): TurndownService {
  if (!turndown) {
    turndown = new TurndownService({
      headingStyle: "atx",
      codeBlockStyle: "fenced",
      bulletListMarker: "-",
      hr: "---",
    });
  }
  return turndown;
}

function inCode(view: EditorView, pos: number): boolean {
  let node = syntaxTree(view.state).resolveInner(pos, -1);
  while (node) {
    if (node.name === "FencedCode" || node.name === "CodeBlock" || node.name === "InlineCode") return true;
    if (!node.parent) break;
    node = node.parent;
  }
  return false;
}

export const smartPaste: Extension = EditorView.domEventHandlers({
  paste(event, view) {
    const clipboard = event.clipboardData;
    if (!clipboard || clipboard.files.length > 0) return false; // attachments handler owns files
    const plain = clipboard.getData("text/plain");
    const html = clipboard.getData("text/html");
    const sel = view.state.selection.main;

    if (inCode(view, sel.from)) return false;

    // URL over a selection -> [selection](url)
    if (!sel.empty && URL_RE.test(plain.trim())) {
      event.preventDefault();
      const label = view.state.sliceDoc(sel.from, sel.to);
      const insert = `[${label}](${plain.trim()})`;
      view.dispatch({
        changes: { from: sel.from, to: sel.to, insert },
        selection: EditorSelection.cursor(sel.from + insert.length),
        userEvent: "input.paste",
      });
      return true;
    }

    // Rich HTML -> markdown (skip when the HTML is just a wrapped span of
    // the same plain text — converting would add nothing)
    if (html && html.length > 40 && /<(h[1-6]|ul|ol|li|table|pre|blockquote|a |img|strong|em|code)/i.test(html)) {
      try {
        const md = stripMarkdownEscapes(getTurndown().turndown(html)).trim();
        if (md && md !== plain.trim()) {
          event.preventDefault();
          view.dispatch(view.state.replaceSelection(md), { userEvent: "input.paste" });
          return true;
        }
      } catch {
        // fall through to the default paste
      }
    }
    return false;
  },
});
